import {isApiTrue}                                              from "@Common/logic/is-api-true";
import {ComponentLink, Link, makeChain, valueHelper as value}   from "@Common/react/cst2";
import {UseState}                                               from "@Common/react/use-state";
import {Types}                                                  from "@Common/types";
import {chain}                                                  from "@Common/utils/fs";

import {consumeContext} from "@Xpresso/loaders/consume-context";

type Item           = Types.Apis.Models.Item;
type OptionsField   = Types.Apis.Payloads.Next.Template.GetVariations.OptionsField;
type Variation      = Types.Apis.Payloads.Next.Template.GetVariations.Variation;

export type Selection = Record<string, string>;

export const VariationSelectionState = ({item, templateBack, templateOptions, next: rootNext}:{
    item: null | Item,
    templateBack: Types.Apis.Payloads.Next.TemplateBack.GetTemplates.Response,
    templateOptions: Types.Apis.Models.TemplateOptions,
    next: Link<JSX.Element, {
        changed: boolean,
        enabled: boolean,
        fields: Array<{
            id: string,
            name: string,
            resource: null | {
                label: string,
                url: string,
            },
            options: Array<{
                id: string,
                label: string,
                isOutOfStock: boolean,
                isSelected: boolean,
                select: () => void,
            }>,
        }>,
        selection: null | {
            isOutOfStock: boolean,
            isValid: boolean,
            matchingVariations: Variation[],
            value: Selection,
        },
        service: ReturnType<typeof VariationSelectionService>,
        setSelection: (selection: Selection) => void,
    }>
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then("isVariationsMaster", value(
        () => templateOptions.options.find(({devKey}) => devKey === "variation_type")?.value === "master"
    ))
    .then("selectionService", value(({isVariationsMaster}) => (
        isVariationsMaster ? VariationSelectionService({
            variations: templateBack.variations,
            optionsFields: templateBack.variationOptionFields.filter((field) => field.type === "options"),
        }) : null
    )))
    .then("savedSelection", value(({isVariationsMaster}) => !item || !isVariationsMaster ? null :
        templateBack.variations.find((variation) => variation.variationIdTemplate === item.sku.variation).fieldValues
    ))
    .then("selectionState", ({savedSelection}, next:ComponentLink<typeof UseState<Selection>>) => (
        UseState({
            next,
            initValue: savedSelection ?? {},
        })
    ))
    .then(({
        isVariationsMaster,
        savedSelection,
        selectionState: {onChange: setSelection, value: selection},
        selectionService,
    }) => rootNext({
        changed: !savedSelection || templateBack.variationOptionFields.some(({id}) => (
            savedSelection[id] !== selection[id]
        )),
        enabled: isVariationsMaster,
        fields: templateBack.variationOptionFields.filter((field) => field.type === "options").map(({id: fieldId, label, name, options, resource}) => ({
            name: label ?? name,
            id: fieldId,
            resource: resource ? {
                label: resource.label,
                url: resource.assetURL,
            } : null,
            options: options.map(({id: optionId, label}) => ({
                id: optionId,
                label,
                isOutOfStock: chain(
                    selection,
                    (selection) => selectionService.withOption(selection, {fieldId, optionId, ifOutOfStock: "ignore"}),
                    selectionService.isOutOfStock,
                ),
                isSelected: selectionService.hasOption(selection, fieldId, optionId),
                select: () => (
                    setSelection(selectionService.withOption(selection, {fieldId, optionId, ifOutOfStock: "pivot"}))
                ),
            })),
        })),
        selection: !isVariationsMaster ? null : {
            isOutOfStock: selectionService.isOutOfStock(selection),
            isValid: selectionService.isValid(selection),
            matchingVariations: selectionService.asMatchingVariations(selection),
            value: selection,
        },
        service: selectionService,
        setSelection,
    }))
    .run()
);

/**
 * Service for manipulating selections. All methods are pure.
 *
 * Selections can be incomplete and match multiple variations, but a
 * valid selection will only map to a single, in-stock one.
 *
 * Selections lack vendor data.
 */
const VariationSelectionService = ({optionsFields, variations}:{
    optionsFields: OptionsField[],
    variations: Variation[],
}) =>
{
    const asMatchingVariations = (selection:Selection): Variation[] => (
        variations.filter((variation) => (
            optionsFields.reduce((isMatch, {id}) => (
                isMatch && (!(id in selection) || variation.fieldValues[id] === selection[id])
            ), true)
        ))
    );

    const hasOption = (selection:Selection, fieldId:string, optionId:string): boolean => (
        fieldId in selection && selection[fieldId] === optionId
    );

    const reduceIntersection = (selection: Selection, target: Selection): Selection => (
        optionsFields.reduce<Selection>((intersection, {id}) => (
            id in selection && id in target && selection[id] === target[id] ? {
                ...intersection,
                [id]: selection[id],
            } : intersection
        ), {})
    );

    const reduceMerge = (selection: Selection, {target, ifOutOfStock="ignore"}:{
        ifOutOfStock: "abort" | "ignore" | "pivot",
        target: Selection,
    }): Selection =>
    {
        const merged = optionsFields.reduce<Selection>((merge, {id}) => (
            id in target
            ? withOption(merge, {fieldId: id, optionId: target[id], ifOutOfStock: "ignore"})
            : merge
        ), selection);

        switch (true) {
            case !isOutOfStock(merged) || ifOutOfStock === "ignore": return merged;
            case ifOutOfStock === "abort": return selection;
            case ifOutOfStock === "pivot": return pivotOutOfStock(merged, Object.keys(target));
        }
    };

    const isComplete = (selection:Selection): boolean => (
        optionsFields.reduce((isComplete, {id}) => (isComplete && id in selection), true)
    );

    const isOutOfStock = (selection:Selection): boolean =>
    {
        const variations = asMatchingVariations(selection);
        // if no variations are "orderable" then it's out of stock
        return !variations.some(variationIsOrderable);
    };

    const isValid = (selection:Selection): boolean => isComplete(selection) && !isOutOfStock(selection);

    const makeFromVariation = (variation:Variation): Selection => (
        optionsFields.reduce<Selection>((selection, {id}) => ({
            ...selection,
            [id]: variation.fieldValues[id],
        }), {})
    );

    const pivotOutOfStock = (selection:Selection, priority?:string[]): Selection =>
    {
        const fieldIds  = [...(priority ?? []), ...Object.keys(selection)].filter((id, index, array) => (
            id in selection
            && optionsFields.some(field => field.id === id)
            && array.indexOf(id) === index // uniques
        ));
        return fieldIds.reduce<Selection>((result, fieldId) =>
        {
            const intermediateResult = {...result, [fieldId]: selection[fieldId]};
            return isOutOfStock(intermediateResult) ? result : intermediateResult;
        }, {});
    };

    const withOption = (
        selection:Selection,
        {fieldId, optionId, ifOutOfStock="ignore"}:{
            fieldId: string,
            optionId: string,
            /** "ignore" to apply option anyway, "abort" to return original selection, "pivot" to pivot to in-stock */
            ifOutOfStock: "abort" | "ignore" | "pivot",
        },
    ): Selection =>
    {
        const selectionWithOption = {...selection, [fieldId]: optionId};
        return (
            ifOutOfStock === "ignore" || !isOutOfStock(selectionWithOption)
            ? selectionWithOption
            : (
                ifOutOfStock === "pivot"
                ? pivotOutOfStock(selectionWithOption, [fieldId])
                : selection
            )
        );
    };

    return {
        /** Map a selection to all matching variations */
        asMatchingVariations,
        /** Check if a selection has an option selected for a field */
        hasOption,
        /** Reduce two selections to their intersection */
        intersect: reduceIntersection,
        /**
         * Selections are out of stock when lacking matched variations, or when all variations are out of stock
         *
         * Use the empty selection {} to check for template-wide out-of-stock
         */
        isOutOfStock,
        /** Selections are valid when they are complete and in-stock */
        isValid,
        /** Create a selection that matches a variation */
        makeFromVariation,
        /** Merge two selections, optionally handling out-of-stock */
        merge: reduceMerge,
        /**
         * Apply an option to a selection, potentially returning a new selection
         *
         * The original selection is returned if the result is out of stock and `ifOutOfStock === "abort"`
         */
        withOption,
    };
};

const variationIsOrderable = (variation:Variation): boolean => (
    isApiTrue(variation.product.allow_back_order)
    || (
        (variation.product.availableQuantity ?? 1) !== 0
        && !isApiTrue(variation.product.out_of_stock)
    )
);

